import React from 'react';
import PropTypes from 'prop-types';
import Badge from '../../../components/ui/Badge';
import useCenterText from '../centerText';

export const initialsOf = (name) => {
  const words = String(name || '').split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return '?';
  return `${words[0][0] || ''}${words.length > 1 ? words[1][0] || '' : ''}`.toLocaleUpperCase('tr-TR');
};

export const isInactive = (center) => center?.['Durum'] === 'İnaktif';

/** Aktif (green) / İnaktif (grey) pill; other stored values are shown as they are. */
export const StatusBadge = ({ value }) => {
  const { statusLabel } = useCenterText();
  if (!value) return <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;
  const active = value === 'Aktif';
  return (
    <Badge tone={active ? 'success' : 'neutral'}>
      <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${active ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
      {statusLabel(value)}
    </Badge>
  );
};

StatusBadge.propTypes = { value: PropTypes.string };

/** Region colour dot; the colour goes through a CSS variable (dark mode resets inline colours). */
export const RegionDot = ({ color, className = 'tw-h-2.5 tw-w-2.5' }) => (
  <span
    aria-hidden="true"
    style={{ '--region': color || '#94a3b8' }}
    className={`tw-inline-block tw-shrink-0 tw-rounded-full tw-bg-[color:var(--region)] tw-ring-2 tw-ring-white/70 dark:tw-ring-white/10 ${className}`}
  />
);

RegionDot.propTypes = { color: PropTypes.string, className: PropTypes.string };

/** Region name with its colour, as a soft pill. */
export const RegionTag = ({ region }) => (
  <span className="tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-slate-900/[0.05] tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-slate-700 dark:tw-bg-white/[0.07] dark:tw-text-slate-200">
    <RegionDot color={region.color} className="tw-h-2 tw-w-2" />
    <span className="tw-truncate">{region.name}</span>
  </span>
);

RegionTag.propTypes = { region: PropTypes.shape({ name: PropTypes.string, color: PropTypes.string }).isRequired };

const PILL_TONES = {
  neutral: 'tw-bg-slate-900/[0.05] tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300',
  muted: 'tw-bg-slate-900/[0.04] tw-text-slate-500 dark:tw-bg-white/[0.05] dark:tw-text-slate-400',
  success: 'tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300',
  amber: 'tw-bg-amber-500/15 tw-text-amber-800 dark:tw-text-amber-200',
  teal: 'tw-bg-teal-500/10 tw-text-teal-700 dark:tw-text-teal-300',
  violet: 'tw-bg-violet-500/10 tw-text-violet-700 dark:tw-text-violet-300',
  sky: 'tw-bg-sky-500/10 tw-text-sky-700 dark:tw-text-sky-300',
};

/** Small count / tag pill (SMO overview, region cards). */
export const Pill = ({ icon: Icon, tone = 'neutral', children }) => (
  <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-tabular-nums ${PILL_TONES[tone]}`}>
    {Icon && <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />}
    {children}
  </span>
);

Pill.propTypes = { icon: PropTypes.elementType, tone: PropTypes.oneOf(Object.keys(PILL_TONES)), children: PropTypes.node };

/** Titled group of form fields. */
export const FormSection = ({ icon: Icon, title, children }) => (
  <section className="tw-space-y-3">
    <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
      <span className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      {title}
    </h3>
    {children}
  </section>
);

FormSection.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node };

/** Renders useCenterText().richText segments, bold where the values sit. */
export const RichText = ({ segments, boldClassName = 'tw-font-semibold tw-text-slate-900 dark:tw-text-white' }) => (
  <>
    {segments.map((segment, index) => (segment.bold
      ? <strong key={index} className={boldClassName}>{segment.text}</strong>
      : <React.Fragment key={index}>{segment.text}</React.Fragment>))}
  </>
);

RichText.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string, bold: PropTypes.bool })).isRequired,
  boldClassName: PropTypes.string,
};
